import { useParams, useSearchParams } from 'react-router-dom';
import { DynamicForm } from '../components/DynamicForm';
import FormWizard from '../components/FormWizard';
import { ProjectStatus } from '../components/ProjectStatus';
import { CustomerApproval } from '../components/CustomerApproval';
import { AppleAgreementUpdate } from '../components/AppleAgreementUpdate';
import { AppStoreSubmission } from '../components/AppStoreSubmission';
import { AppStoreApprovalWaiting } from '../components/AppStoreApprovalWaiting';
import { DeploymentFinished } from '../components/DeploymentFinished';
import { StageForUpdate } from '../components/StageForUpdate';
import { TestingLinksPhase } from '../components/TestingLinksPhase';
import { WaitingForModelFile } from '../components/WaitingForModelFile';
import { useSecureAuth } from '../hooks/useSecureAuth';
import { useProject } from '../hooks/useProject';
import { ExpiredSessionForm } from '../components/ExpiredSessionForm';
import { formConfigs } from '../config/formConfigs';
import { ProjectData } from '../types';
import { Loader2, Bell, LogOut, Shield } from 'lucide-react';
import { useState } from 'react';
import { environment } from '../config/environment';
import { GlobalNotification } from '../components/GlobalNotification';
import { useNotificationContext } from '../contexts/NotificationContext';
import { NotificationPreferencesModal } from '../components/NotificationPreferencesModal';
import {
  getCustomerTokenVerificationError,
  shouldPromptCustomerTokenContinue,
} from '../utils/customerMagicLink';

export const ProjectPage = () => {
  const { jiraKey } = useParams<{ jiraKey: string }>();
  const [searchParams] = useSearchParams();
  const { loading: authLoading, sessionExpired, signOut, createSession, user } = useSecureAuth();
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [tokenConsumed, setTokenConsumed] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const { notifications, removeNotification } = useNotificationContext();

  const token = searchParams.get('token');
  const showContinueGate = shouldPromptCustomerTokenContinue(token, tokenConsumed);

  // Only call useProject if we have a valid session (not loading auth, not session expired, and no token error)
  const shouldLoadProject =
    !authLoading && !sessionExpired && !tokenError && !showContinueGate && (user || tokenConsumed);
  const { project, loading: projectLoading, error: projectError, updateFormData } = useProject(
    shouldLoadProject ? jiraKey || '' : ''
  );

  // IS-1697: only verify on explicit user click so email scanners cannot burn one-time tokens
  const handleContinueWithToken = async () => {
    if (!token || tokenConsumed) {
      return;
    }

    setVerifyingToken(true);
    setTokenConsumed(true);

    try {
      const functionsUrl = environment.useEmulators
        ? `http://127.0.0.1:5001/ima-onboard/us-central1/verifyMagicLink`
        : `https://us-central1-ima-onboard.cloudfunctions.net/verifyMagicLink`;

      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();
      const verificationError = getCustomerTokenVerificationError(response.ok, result);

      if (!verificationError) {
        await createSession(result.email, result.jiraKey, result.customToken);
        window.history.replaceState({}, document.title, `/project/${result.jiraKey || jiraKey}`);
      } else {
        setTokenError(verificationError);
      }
    } catch (error) {
      console.error('Error verifying magic link:', error);
      setTokenError('Failed to verify magic link. Please try again.');
    } finally {
      setVerifyingToken(false);
    }
  };

  // Show loading state while checking authentication, verifying token, or loading project
  if (authLoading || verifyingToken || (shouldLoadProject && projectLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">
            {verifyingToken
              ? 'Verifying your access...'
              : authLoading
                ? 'Checking authentication...'
                : 'Loading project...'}
          </p>
        </div>
      </div>
    );
  }

  if (showContinueGate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Continue to Your Portal</h3>
          <p className="text-sm text-gray-500 mb-6">
            Click below to open your onboarding portal. This helps prevent email security scanners
            from using your access link before you do.
          </p>
          <button
            type="button"
            onClick={handleContinueWithToken}
            className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Continue to Portal
          </button>
        </div>
      </div>
    );
  }

  // Show error message if token verification failed (expired or invalid link)
  if (tokenError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Access Link Unavailable
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {tokenError}
              </p>
              <p className="text-sm text-gray-500">
                For security reasons, access links expire after a certain period.
                Please request a new link to continue.
              </p>
            </div>
            <ExpiredSessionForm jiraKey={jiraKey || ''} />
          </div>
        </div>
      </div>
    );
  }

  // Show session expired form if no valid session
  if (sessionExpired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full">
          <ExpiredSessionForm jiraKey={jiraKey || ''} />
        </div>
      </div>
    );
  }

  // Show loading state while fetching project
  if (projectLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading your project...</p>
        </div>
      </div>
    );
  }

  // Show error state if project failed to load
  if (projectError || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Error Loading Project</p>
            <p className="text-sm">{projectError || 'Project not found'}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Get form configuration based on app type
  const formConfig = formConfigs[project.appType];
  if (!formConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Invalid app type: {project.appType === 'versirent' ? 'VersiRent App' : project.appType === 'idealone' ? 'IdealOne' : project.appType}</p>
        </div>
      </div>
    );
  }


  // Handle form submission
  const handleFormSubmit = async (formData: Partial<ProjectData['formData']>) => {
    try {
      await updateFormData(formData);
      // Form submission successful
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  // Handle customer approval
  const handleCustomerApproval = async (approved: boolean, comments?: string) => {
    try {
      // Call the backend to update customer approval
      const response = await fetch(`https://us-central1-ima-onboard.cloudfunctions.net/updateCustomerApproval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jiraKey: jiraKey,
          approved,
          comments,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update customer approval');
      }

      // Refresh the project data to get the updated status
      window.location.reload();
    } catch (error) {
      console.error('Error updating customer approval:', error);
      alert('Failed to update approval. Please try again.');
    }
  };


  const isAdminViewer = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin viewing banner: admins reach project pages via their own session,
          so surface a clear way back to the admin portal without burning a link. */}
      {isAdminViewer && (
        <div className="bg-amber-500 text-white text-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Admin view — you are viewing this customer&apos;s portal.
            </span>
            <a
              href="/admin"
              className="font-medium underline underline-offset-2 hover:text-amber-100 whitespace-nowrap"
            >
              ← Back to Admin Portal
            </a>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img
                src="/ima_onboard_logo.png"
                alt="Ideal Mobile App Logo"
                className="h-8 w-auto"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {project.appType === 'versirent' ? 'VersiRent App' : 'IdealOne'} Onboarding
                </h1>
                <p className="text-sm text-gray-500">Project: {jiraKey}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsNotificationModalOpen(true)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 text-sm"
              >
                <Bell className="h-4 w-4" />
                <span>Notifications</span>
              </button>
              <button
                onClick={signOut}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 text-sm"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Project Status */}
        <ProjectStatus status={project.status} />

        {/* Main Content - Wizard Layout */}
        <div className="max-w-4xl mx-auto">
          {/* IS-1638: only treat as submitted when pipeline finished (lastNotifiedStatus set) */}
          {(project.status === 'stage1_awaiting_customer' ||
            (project.status === 'stage2_provisioning' &&
              project.lastNotifiedStatus !== 'stage2_provisioning')) &&
          jiraKey ? (
            <FormWizard
              appType={project.appType}
              jiraKey={jiraKey}
              onComplete={handleFormSubmit}
              user={user}
            />
          ) : project.status === 'customer_review' ? (
            <CustomerApproval
              project={project}
              onApprove={handleCustomerApproval}
            />
          ) : project.status === 'stage5_testing' ? (
            <TestingLinksPhase project={project} jiraKey={jiraKey || ''} />
          ) : project.status === 'waiting_for_model_file' ||
            project.status === 'inventory_clean_up_required' ? (
            <WaitingForModelFile
              inventoryCleanUp={project.status === 'inventory_clean_up_required'}
            />
          ) : project.status === 'apple_agreement_update_needed' ? (
            <AppleAgreementUpdate project={project} />
          ) : project.status === 'submit_to_app_store' ? (
            <AppStoreSubmission project={project} />
          ) : project.status === 'waiting_on_app_store_approval' ? (
            <AppStoreApprovalWaiting project={project} />
          ) : project.status === 'deployment_finished' ? (
            <DeploymentFinished
              project={project}
              appStoreLinks={{
                // These would come from project data or configuration
                apple: 'https://apps.apple.com/app/your-app',
                google: 'https://play.google.com/store/apps/details?id=com.yourcompany.yourapp'
              }}
            />
          ) : project.status === 'stage_for_update' ? (
            <StageForUpdate
              project={project}
              updateDetails={{
                version: '2.1.0',
                features: [
                  'Enhanced user interface with improved navigation',
                  'Advanced analytics and reporting features',
                  'New notification system for better communication'
                ],
                improvements: [
                  'Faster app loading times',
                  'Improved security and data protection',
                  'Better mobile responsiveness',
                  'Bug fixes and stability improvements'
                ],
                migrationDate: 'December 15, 2024'
              }}
            />
          ) : (
            <DynamicForm
              fields={formConfig.fields}
              initialData={project.formData}
              onFormChange={handleFormSubmit}
              jiraKey={jiraKey || ''}
              readonly={true}
            />
          )}
        </div>

        {/* Help Section */}
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Need Help?</h3>
            <div className="space-y-3">
              <a
                href={`/project/${jiraKey}/help`}
                className="flex items-center justify-center space-x-3 w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                <span className="text-lg">📚</span>
                <span className="font-medium">Get Help & Documentation</span>
                <span className="text-blue-200 group-hover:text-blue-100 transition-colors">→</span>
              </a>
              <div className="text-xs text-gray-500 text-center">
                <p>Find answers, guides, and resources for your {project.appType === 'versirent' ? 'VersiRent App' : 'IdealOne'} setup</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Global Notifications */}
      <GlobalNotification 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      
      {/* Notification Preferences Modal */}
      <NotificationPreferencesModal
        project={project}
        onUpdate={updateFormData}
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </div>
  );
};
